import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import TraegerProfileEditor from "@/components/TraegerProfileEditor";
import MeasureApprovalsTab from "@/components/MeasureApprovalsTab";
import CcSettingsTab from "@/components/CcSettingsTab";

const STEPS = [
  ["profile", "Trägerdaten", "Name, Anschrift, Kontakt und Rechtliches – erscheinen in allen E-Mails, PDFs und Trägerbestätigungen."],
  ["images", "Logo, Stempel & Unterschrift", "Logo für E-Mails/PDFs, Stempel und Unterschrift für die Trägerbestätigung."],
  ["approvals", "Maßnahmen-Zulassungen", "Maßnahmenummer und Zulassungszeiträume je Maßnahme – am schnellsten per Übersichtsbogen-Upload."],
  ["cc", "CC-Mitarbeiter", "Wer erhält Kopien beim Rechnungsversand und bei Trägerbestätigungen?"],
];

export default function SetupWizardPage() {
  const [params, setParams] = useSearchParams();
  const stepIdx = Math.max(0, STEPS.findIndex(([k]) => k === params.get("step")));
  const [i, setIdx] = useState(stepIdx);
  const setI = (n) => { setIdx(n); setParams({ step: STEPS[n][0] }, { replace: true }); };
  useEffect(() => { setIdx(stepIdx); }, [stepIdx]);
  const [status, setStatus] = useState(null);
  const load = useCallback(() => api.get("/setup/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load, i]);
  const done = (k) => status?.steps.find((s) => s.key === k)?.done;
  const [key, label, hint] = STEPS[i];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-6" data-testid="setup-wizard-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Einrichtung</div>
        <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900">Einrichtungs-Assistent</h1>
        <p className="text-sm text-slate-500 mt-1">In vier Schritten ist das Portal für deinen Bildungsträger startklar. Alles lässt sich später im Management ändern.</p>
      </div>
      <ol className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="setup-steps">
        {STEPS.map(([k, l], idx) => (
          <li key={k}>
            <button onClick={() => setI(idx)} data-testid={`setup-step-${k}`}
              className={`w-full h-full min-h-[44px] text-left border px-3 py-2 text-sm flex items-center gap-2 transition-colors ${idx === i ? "border-slate-900 bg-white" : "border-slate-200 bg-white/60 hover:bg-white"}`}>
              {done(k) ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <Circle size={16} className="text-slate-300 shrink-0" />}
              <span className="truncate"><span className="text-slate-400 mr-1">{idx + 1}.</span>{l}</span>
            </button>
          </li>
        ))}
      </ol>
      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-medium text-slate-900">{i + 1}. {label}</h2>
        <p className="text-sm text-slate-500">{hint}</p>
        {key === "profile" && <TraegerProfileEditor show="fields" onSaved={load} />}
        {key === "images" && <TraegerProfileEditor show="images" onProfile={load} />}
        {key === "approvals" && <MeasureApprovalsTab />}
        {key === "cc" && <CcSettingsTab />}
      </section>
      <div className="flex items-center justify-between border-t pt-4">
        <button onClick={() => setI(i - 1)} disabled={i === 0} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm disabled:opacity-40" data-testid="setup-prev"><ArrowLeft size={14} /> Zurück</button>
        {i < STEPS.length - 1 ? (
          <button onClick={() => setI(i + 1)} className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2 text-sm hover:bg-slate-700" data-testid="setup-next">Weiter <ArrowRight size={14} /></button>
        ) : (
          <Link to="/" className="inline-flex items-center gap-2 bg-[#0a788c] text-white px-5 py-2 text-sm" data-testid="setup-finish">{status?.complete ? "Fertig – zum Dashboard" : "Später fortsetzen"}</Link>
        )}
      </div>
    </div>
  );
}
